import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  catalogResponseSchema,
  templateResponseSchema,
  templatesResponseSchema,
  type CatalogItem,
  type FormTemplate,
} from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPatch, apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { itemName } from '../inventory/itemName.js';
import { LineEditor, type Line } from './LineEditor.js';
import { linesFromTemplate } from './TemplateSelect.js';

const input =
  'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';
const primary =
  'rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

/**
 * Form templates, the storeroom's editor (owner, 2026-09-13): name a list of
 * items once, pick it in any issue wizard later. No delete — deactivate.
 */
export function TemplatesPage() {
  const { t, i18n } = useTranslation();
  const templates = useApi('/templates?all=true', templatesResponseSchema);
  const catalog = useApi('/catalog', catalogResponseSchema);
  const items = useMemo(() => (catalog.kind === 'ready' ? catalog.data.items.filter((i) => i.active) : []), [catalog]);
  const [editing, setEditing] = useState<FormTemplate | 'new' | null>(null);

  if (templates.kind === 'loading' || catalog.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (templates.kind === 'error') return <ApiError code={templates.code} requestId={templates.requestId} />;
  if (catalog.kind === 'error') return <ApiError code={catalog.code} requestId={catalog.requestId} />;

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="templates-page">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">{t('templates.title')}</h2>
        {editing === null && (
          <button type="button" onClick={() => setEditing('new')} className={primary} data-testid="template-new">
            {t('templates.new')}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('templates.hint')}</p>

      {editing !== null && (
        <TemplateForm
          template={editing === 'new' ? null : editing}
          items={items}
          onDone={() => {
            setEditing(null);
            templates.reload();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {templates.data.templates.length === 0 && (
        <p className="mt-3 text-sm text-slate-500" data-testid="templates-empty">{t('templates.empty')}</p>
      )}
      <ul className="mt-3 divide-y divide-slate-100">
        {templates.data.templates.map((tpl) => (
          <TemplateRow key={tpl.id} template={tpl} items={items} language={i18n.language} onEdit={() => setEditing(tpl)} onChanged={templates.reload} />
        ))}
      </ul>
    </section>
  );
}

function TemplateRow({
  template,
  items,
  language,
  onEdit,
  onChanged,
}: {
  template: FormTemplate;
  items: CatalogItem[];
  language: string;
  onEdit: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const byId = new Map(items.map((i) => [i.id, i]));
  const toggle = useAction(() => apiPatch(`/templates/${template.id}`, templateResponseSchema, { active: !template.active }));

  return (
    <li className="py-3" data-testid="template-row" data-id={template.id} data-active={template.active}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-slate-900">
          {template.name}
          {!template.active && <span className="ms-2 text-xs text-slate-500">{t('templates.inactive')}</span>}
        </span>
        <span className="flex gap-2">
          <button type="button" onClick={onEdit} className={secondary} data-testid="template-edit">
            {t('templates.edit')}
          </button>
          <button type="button" onClick={() => void toggle.run().then((r) => r && onChanged())} disabled={toggle.busy} className={secondary} data-testid="template-toggle">
            {template.active ? t('templates.deactivate') : t('templates.activate')}
          </button>
        </span>
      </div>
      <ul className="mt-1 text-sm text-slate-700">
        {template.lines.map((l, i) => {
          const item = byId.get(l.catalogItemId);
          return (
            <li key={i}>
              {item ? itemName(item, language) : l.catalogItemId}
              {item?.tracking === 'quantity' && <span className="latin-inline"> × {l.quantity}</span>}
              {item?.tracking === 'serialized' && <span className="text-xs text-slate-500"> · {t('templates.oneEach')}</span>}
            </li>
          );
        })}
      </ul>
      <ActionError error={toggle.error} />
    </li>
  );
}

function TemplateForm({
  template,
  items,
  onDone,
  onCancel,
}: {
  template: FormTemplate | null;
  items: CatalogItem[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(template?.name ?? '');
  const [lines, setLines] = useState<Line[]>(() => (template ? linesFromTemplate(template, items) : []));
  const body = () => ({
    name: name.trim(),
    lines: lines.map((l) => ({ catalogItemId: l.item.id, quantity: l.item.tracking === 'serialized' ? 1 : l.quantity, condition: l.condition, notes: l.notes.trim() || null })),
  });
  const save = useAction(() =>
    template
      ? apiPatch(`/templates/${template.id}`, templateResponseSchema, body())
      : apiPost('/templates', templateResponseSchema, body()),
  );

  return (
    <div className="mt-3 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-testid="template-form">
      <label className="block text-sm text-slate-700">
        {t('templates.name')}
        <input className={`${input} mt-1 block w-full`} value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name" />
      </label>
      <LineEditor lines={lines} onChange={setLines} items={items} withSerials={false} testId="template" />
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => void save.run().then((r) => r && onDone())}
          disabled={save.busy || name.trim() === '' || lines.length === 0}
          className={primary}
          data-testid="template-save"
        >
          {t('templates.save')}
        </button>
        <button type="button" onClick={onCancel} className={secondary}>
          {t('org.cancel')}
        </button>
      </div>
      <div className="mt-2">
        <ActionError error={save.error} />
      </div>
    </div>
  );
}
